<!--
    ag-grid-vue 的属性和事件说明，具体参考官方文档：https://www.ag-grid.com/vue-data-grid
    :columnDefs="column" // 列定义
    :rowData="rowData" // 行数据
    :rowSelection="rowSelection" // 行选择类型 'single' or 'multiple'
    :rowDragManaged="false" // 是否启用行拖拽
    :localeText="localeText" // 国际化文本
    :animateRows="true" // 是否启用行动画
    :enableColResize="true" // 是否启用列宽调整
    :suppressRowClickSelection="true" // 是否禁用行点击选择
    :defaultColDef="defaultColDef" // 默认列定义
    :suppressContextMenu="false" // 是否禁用右键菜单
    :enableCellTextSelection="true" // 是否启用单元格文本选择
    :ensureDomOrder="true" // 是否确保DOM顺序
    :enableRangeSelection="true" // 范围选择，使表格拥有excel的功能
    :enableFillHandle="true" // 允许向下填充，需要开启表格可编辑才会生效 editable: true,
    :getRowClass="getRowClass" // 行样式
    :getRowClass="getRowClass" // 行样式
    :getMainMenuItems="getMainMenuItems" // 获取表头的菜单项
    :tooltipShowDelay="500" // 提示显示延迟
    v-bind="$attrs" // 绑定属性
    v-on="$listeners" // 绑定事件
    @grid-ready="onGridReady" // 表格准备就绪事件
    @row-selected="onRowSelected" // 单选
    @selection-changed="onSelectionChanged" // 选择事件
    @sort-changed="onSortChanged" // 排序变化
-->
<template>
    <div class="custom-ag" :class="{'card-mode': isCard}">
        <div class="custom-ag_header">
            <slot name="header"></slot>
        </div>
        <ag-grid-vue
            ref="gridRef"
            v-Mdloading="loading"
            :style="{'height': tableHeight}"
            headerHeight="32"
            rowHeight="30"
            class="ag-theme-alpine"
            :class="{ 'ag-theme-custom': useCustomTheme }"
            :columnDefs="column"
            :rowData="rowData"
            :rowSelection="rowSelection"
            :rowDragManaged="false"
            :localeText="localeText"
            :animateRows="true"
            :enableColResize="true"
            :suppressRowClickSelection="true"
            :defaultColDef="defaultColDef"
            :suppressContextMenu="false"
            :enableRangeSelection="true"
            :enableFillHandle="enableFillHandle"
            :enableCellTextSelection="true"
            :ensureDomOrder="true"
            :getRowClass="getRowClass"
            :getMainMenuItems="getMainMenuItems"
            :tooltipShowDelay="500"
            :isRowSelectable="isRowSelectable"
            :getRowId="getRowId"
            v-bind="$attrs"
            v-on="$listeners"
            @grid-ready="onGridReady"
            @row-selected="onRowSelected"
            @selection-changed="onSelectionChanged"
            @sort-changed="onSortChanged"
            @columnMoved="onColumnMoved"
            @columnVisible="onColumnVisible"
            @columnPinned="onColumnPinned"
            @first-data-rendered="onFirstDataRendered"
            @state-updated="onStateUpdated"
            @ViewportChangedEvent="ViewportChangedEvent"
            @row-data-updated="onRowDataUpdated"
        >
        </ag-grid-vue>
        <div class="custom-ag_footer">
            <slot name="footer"></slot>
        </div>
    </div>
</template>
<!-- eslint-disable vue/no-unused-components -->
<script>
import { AgGridVue } from 'ag-grid-vue';
import localeText from './lib/localeText.js'; // 国际化
import './lib/ag-grid-enterprise.js'; // 企业版
import RenderSlot from './components/renderSlot.vue';
import renderHeaderSlot from './components/renderHeaderSlot.vue';
import scrollBar from '@/utils/table-scroll-bar-grid.js'; // 固定滚动条
import sticky from '@/utils/sticky-grid.js'; // 固定表头

export default {
    mixins: [scrollBar, sticky],
    components: {
        AgGridVue,
        RenderSlot,
        columnHeader: renderHeaderSlot, // 头部自定义
    },
    props: {
        rowData: { // 表格数据
            type: Array,
            default: () => [],
        },
        columnsForIview: { // iview表格列配置，用于旧页面更换Table为ag-grid-vue, 会做一层转换
            type: Array,
            default: () => [],
        },
        columnDefs: { // 原生列定义
            type: Array,
            default: () => [],
        },
        rowSelection: { // 行选择类型: 'single' or 'multiple'
            type: String,
            default: 'multiple',
        },
        height: { // 表格高度, 可传入数字或字符串，字符串有无px都可以
            type: [String, Number],
            default: '',
        },
        rowClassName: { // 行样式
            type: Function,
            default: () => '',
        },
        loading: { // 表格加载状态
            type: Boolean,
            default: false,
        },
        isNeedScrollBar: { // 是否需要固定滚动条 , 在mixins: [scrollBar],中使用
            type: Boolean,
            default: false,
        },
        isNeedSticky: { // 是否需要固定表头 , 在mixins: [sticky],中使用
            type: Boolean,
            default: false,
        },
        useCustomTheme: { // 是否使用自定义主题, 主要为了把表格样式设置成iview表格样式, 默认开启
            type: Boolean,
            default: true,
        },
        cacheKey: { // 缓存列状态的key值
            type: String,
            default: '',
        },
        bottom: { // 表格距离底部距离，包含分页组件高度+上下间距
            type: Number,
            default: 62,
        },
        isCard: { // 是否卡片模式
            type: Boolean,
            default: true,
        },
        /* 默认勾选行 */
        isRowSelectable: {
            // type: Function,
            // default: () => '',
        },
        getRowId: {},
        enableFillHandle: { // 是否开启向下填充功能
            type: Boolean,
            default: false,
        },
    },
    computed: {
        column() {
            if (this.columnsForIview.length) {
                // iview表格配置转换成ag-grid-vue配置
                const res = this.columnsForIview.map((item, index) => {
                    const {
                        type, title, key, width, minWidth, align = 'center', sortable, fixed, render, renderHeader, headerTooltip,
                        rowSpan, colSpan, cellClassRules, ...others
                    } = item;
                    const { cacheIndex, cacheHide, cachePinned } = this.getCacheItem(item); // 获取缓存的列状态
                    if (type === 'selection') {
                        return {
                            headerName: '',
                            field: '',
                            width: 30,
                            minWidth: 30,
                            checkboxSelection: true,
                            filter: false,
                            headerCheckboxSelection: true,
                            showDisabledCheckboxes: true,
                        };
                    }
                    return {
                        headerName: title, // 列名
                        headerTooltip: headerTooltip || title, // 列提示
                        field: key, // 字段名
                        hide: cacheHide || width === 0, // 是否隐藏
                        width: width !== undefined ? width + 15 : undefined, // 列宽 +15是为了显示ag-grid的控件宽度
                        flex: null, // 列宽比例
                        minWidth: minWidth !== undefined ? minWidth + 30 : undefined, // 最小列宽
                        unSortIcon: sortable, // 是否显示排序图标
                        pinned: cachePinned || fixed, // 固定列 left, right
                        cellRenderer: render ? 'RenderSlot' : undefined, // 单元格渲染
                        cellRendererParams: { // 单元格渲染参数
                            render,
                        },
                        headerComponent: renderHeader ? 'columnHeader' : undefined, // 头部渲染
                        headerComponentParams: renderHeader ? { renderHeader: renderHeader } : null, // 头部渲染参数
                        cacheIndex: cacheIndex || index, // 本地缓存的列序号
                        align,  // 对齐方式
                        sortable, // 是否可排序
                        rowSpan, // 行合并
                        colSpan, // 列合并
                        cellClassRules, // 单元格样式
                        ...others, // 其他配置
                    };
                });
                // 按本地序号排序
                res.sort((a, b) => a.cacheIndex - b.cacheIndex);
                return res;
            }
            return this.columnDefs;
        },
    },
    data() {
        return {
            gridApi: null,
            columnApi: null,
            localeText: localeText,  // 国际化
            defaultColDef: { // 默认列配置
                resizable: true,
                filter: false, // 前端筛选
                sortable: true, // 排序
                editable: this.enableFillHandle, // 表格是否可编辑
                width: 100,
                wrapHeaderText: true, // 表头是否能换行，与autoHeaderHeight配合使用
                autoHeaderHeight: true, // 自动设置标题高度
                // tooltipValueGetter: (p) => p.value, // 内容tooltip，内容为render时会显示[object object]，暂时不用
            },
            columnStateCache: null,
            columnStateCacheKey: this.$route.name + this.cacheKey, // 列状态缓存的key值
            // 表格行样式，适配iview写法
            getRowClass: params => {
                if (this.rowClassName) {
                    return this.rowClassName(params.data, params.rowIndex);
                }
                return '';
            },
            // 自定义表头菜单
            getMainMenuItems: (params) => {
                params.defaultItems.pop(); // 删除自带的“恢复网格样式”
                params.defaultItems.push({
                    name: this.$t('恢复网格样式'),
                    action: () => {
                        this.columnStateCache = null;
                        this.saveColumnState([]);
                        this.$nextTick(() => {
                            this.columnApi.resetColumnState();
                        });
                    },
                });
                // params.defaultItems.push({
                //     name: this.$t('个性化保存'),
                //     action: () => {
                //         this.saveColumnState();
                //     },
                // });
                return params.defaultItems;
            },
            tableRefName: 'gridRef', // 用于固定滚动条
            parent: 'ag-theme-alpine', // 用于固定表头
            localColumnState: {}, // 本地缓存列状态
            tableHeight: '', // 表格高度
        };
    },
    methods: {
        heightReset() {
            this.$nextTick(() => {
                const table = this.$refs.gridRef;
                if (this.height) {
                    this.tableHeight = this.height > 0 ? `${this.height}px` : this.height;
                } else {
                    const { top } = table.$el.getBoundingClientRect();
                    if (top > 0) {
                        this.tableHeight = `${window.innerHeight - top - this.bottom}px`;
                    }
                }
            });
        },
        onGridReady(params) {
            this.gridApi = params.api;
            this.columnApi = params.columnApi;
            console.log(params, this.gridApi, 'onGridReady');
            this.$emit('onGridReady', this.gridApi);
        },
        // 单选
        onRowSelected(params) {
            console.log(params, 'onRowSelected');
        },
        // 一般用这个即可
        onSelectionChanged(event) {
            const selection = event.api.getSelectedNodes().map((item) => item.data);
            this.$emit('selection-change', selection);
        },
        // 排序变化
        onSortChanged(event) {
            console.log(event, 'onSortChanged');
            const res = { key: '', order: '' };
            event.columnApi.getColumnState().some((item) => {
                if (item.sort) {
                    res.key = item.colId;
                    res.order = item.sort;
                    return true;
                }
                return false;
            });
            this.$emit('sort-change', res);
        },
        // 列移动
        onColumnMoved(params) {
            if (!params.finished) { // 拖动中
                return;
            }
            // 手动操作类型才保存，区分还原时触发的事件；
            if (params.source === 'uiColumnMoved') {
                this.saveColumnState();
            }
            console.log(params, 'onColumnMoved');
        },
        // 列显示隐藏
        onColumnVisible(params) {
            console.log(params, 'onColumnVisible');
            // 手动操作类型才保存，区分还原时触发的事件；
            if (params.source === 'toolPanelUi') {
                this.saveColumnState();
            }
        },
        // 列固定
        onColumnPinned(params) {
            console.log(params, 'onColumnPinned');
            // 手动操作类型才保存，区分还原时触发的事件；
            if (params.source === 'contextMenu') {
                this.saveColumnState();
            }
        },
        // 设置列状态
        saveColumnState(customValue) {
            let stateCache = JSON.parse(localStorage.getItem('columnState'));
            const columnState = customValue || this.columnApi.getColumnState();
            if (stateCache) {
                stateCache[this.columnStateCacheKey] = columnState;
            } else {
                stateCache = { [this.columnStateCacheKey]: columnState };
            }
            localStorage.setItem('columnState', JSON.stringify(stateCache));
        },
        // 设置列状态
        getCacheItem(item) {
            if (!this.columnStateCache) {
                this.columnStateCache = JSON.parse(localStorage.getItem('columnState'));
            }
            const res = {};
            if (this.columnStateCache && this.columnStateCache[this.columnStateCacheKey]) {
                this.columnStateCache[this.columnStateCacheKey].some((column, index) => {
                    if (column.colId === item.key) {
                        res.cacheIndex = index;
                        res.cacheHide = column.hide;
                        res.cachePinned = column.pinned;
                        return true;
                    }
                    return false;
                });
            }
            return res;
        },
        onFirstDataRendered(params) {
            this.$emit('onFirstDataRendered', params);
        },
        onStateUpdated(params) {
            this.$emit('onStateUpdated', params);
        },
        ViewportChangedEvent(params) {
            this.$emit('ViewportChangedEvent', params);
        },
        onRowDataUpdated(params) {
            this.$emit('onRowDataUpdated', params);
        },
        getApi() {
            return this.$refs.gridRef;
        },
    },
    mounted() {
        this.heightReset();
        // 监听窗口变化
        window.addEventListener('resize', this.heightReset);
    },
};
</script>
<style>
@import "~ag-grid-community/styles/ag-grid.css";
@import "~ag-grid-community/styles/ag-theme-alpine.css";

/* ag表格样式尽量与iview表格保持一致 */
.ag-theme-custom {
    --ag-cell-horizontal-padding: 5px;
    --ag-font-size: 11px;
    --ag-cell-horizontal-border: solid #e3e3e3;
}
</style>

<!-- ag组件的样式 -->
<style lang="less">
.scrollBarheight() {
    height: 20px !important;
    max-height: 20px !important;
    min-height: 20px !important;
}

// --- 设置滚动条高度
.ag-body-horizontal-scroll {
    .scrollBarheight();
    .ag-body-horizontal-scroll-viewport {
        .scrollBarheight();
    }
    .ag-horizontal-left-spacer {
        .scrollBarheight();
    }
    .ag-horizontal-right-spacer {
        .scrollBarheight();
    }

}
.ag-body-horizontal-scroll-viewport::-webkit-scrollbar,
.ag-horizontal-left-spacer::-webkit-scrollbar,
.ag-horizontal-right-spacer::-webkit-scrollbar {
  height: 20px;
}

.ag-theme-custom {
    .ag-sort-indicator-icon {
        padding-left: 0px !important;
    }
    .ag-cell-label-container {
        padding: 2px 0px;
    }
}
// -------------------

// 头部菜单框
.ag-popup-child {
    z-index: 1000;
}

// --- 表格合并样式，如果涉及内容超长，需要到功能页面单独设置，超出多少行显示省略号，这与你设置了多少行合并有关；可全局搜索ag-cell-value参考
.ag-ltr .ag-cell.cell-span {
    border-right: var(--ag-row-border-style) var(--ag-row-border-color) var(--ag-row-border-width);
}
.cell-span {
    background-color: #fff;
    border-bottom: var(--ag-row-border-style) var(--ag-row-border-color) var(--ag-row-border-width);
    display: flex;
    align-items: center;
    justify-content: center;
    white-space: normal;
}
// -------------------

</style>

<!-- 本组件的样式 -->
<style lang="less" scoped>
.custom-ag {
    /deep/.md-loading-small {
        z-index: 999;
    }
}
.custom-ag.card-mode {
    background-color: #fff;
    padding: 10px;
    border-radius: 4px;
    .custom-ag_header {
        margin-bottom: 10px;
        /deep/.m-btn {
            margin-right: 3px;
        }
    }
}

</style>
